import React, { useState, useEffect } from 'react';
import { copyToClipboard, getCopyIcon } from '../../utils/copyUtils';

const ActivityTimeline = ({
  activities = [],
  loading = false,
  onRefresh = null,
  onViewCall = null,
  onAddActivity = null,
  onFetchActivityWithPayload = null,
  title = "Activities Timeline",
  autoRefreshInterval = null,
  showAutoRefresh = false,
  emptyMessage = "No activities yet",
  isAnkrAdmin = false
}) => {
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(false);
  const [expandedPayloads, setExpandedPayloads] = useState({});
  const [loadingPayloads, setLoadingPayloads] = useState({});
  const [payloadData, setPayloadData] = useState({});
  const [payloadErrors, setPayloadErrors] = useState({});
  const [copyIconStates, setCopyIconStates] = useState({});
  const [expandedCommunications, setExpandedCommunications] = useState({});

  const togglePayload = async (activityId) => {
    const isExpanding = !expandedPayloads[activityId];
    setExpandedPayloads(prev => ({ ...prev, [activityId]: isExpanding }));

    // If expanding and we don't have the payload yet, fetch it
    if (isExpanding && !payloadData[activityId] && onFetchActivityWithPayload) {
      setLoadingPayloads(prev => ({ ...prev, [activityId]: true }));
      setPayloadErrors(prev => ({ ...prev, [activityId]: null }));
      try {
        const fullActivity = await onFetchActivityWithPayload(activityId);
        // Store the full activity data (could have activity_metadata or retell_data)
        if (fullActivity) {
          setPayloadData(prev => ({ ...prev, [activityId]: fullActivity }));
        }
      } catch (error) {
        setPayloadErrors(prev => ({ ...prev, [activityId]: error.response?.data?.message || error.message || 'Failed to load webhook payload' }));
      } finally {
        setLoadingPayloads(prev => ({ ...prev, [activityId]: false }));
      }
    }
  };

  const toggleCommunication = (activityId) => {
    setExpandedCommunications(prev => ({ ...prev, [activityId]: !prev[activityId] }));
  };

  const hasCommunicationData = (activity) => {
    return activity?.novu_data?.communications && activity.novu_data.communications.length > 0;
  };

  const getCommunicationChannels = (activity) => {
    if (!hasCommunicationData(activity)) return [];
    return activity.novu_data.communications.map(comm => comm.channel);
  };

  // Auto-refresh effect
  useEffect(() => {
    if (!autoRefreshEnabled || !onRefresh || !autoRefreshInterval) return;

    const interval = setInterval(() => {
      onRefresh();
    }, autoRefreshInterval);

    return () => clearInterval(interval);
  }, [autoRefreshEnabled, onRefresh, autoRefreshInterval]);

  const getStatusBadge = (status) => {
    const statusMap = {
      'completed': 'bg-success',
      'in_progress': 'bg-warning',
      'pending': 'bg-secondary',
      'failed': 'bg-danger',
      'registered': 'bg-info',
      'ended': 'bg-success',
      'ongoing': 'bg-warning',
      'error': 'bg-danger'
    };
    return statusMap[status?.toLowerCase()] || 'bg-secondary';
  };

  const getActivityTypeBadge = (activityType) => {
    const typeMap = {
      'call': 'bg-primary',
      'webhook': 'bg-info',
      'system': 'bg-secondary',
      'manual': 'bg-success'
    };
    return typeMap[activityType?.toLowerCase()] || 'bg-info';
  };

  return (
    <div className="card card-body p-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h6 className="card-title text-primary mb-0 border-0">{title}</h6>
        <div className="d-flex gap-2">
          {onAddActivity && (
            <button
              className="btn btn-sm btn-danger py-0 px-2"
              onClick={onAddActivity}
              title="Take action on this event"
            >
              <i className="fa fa-plus me-1"></i>
              Take Action
            </button>
          )}
          {showAutoRefresh && onRefresh && autoRefreshInterval && (
            <button
              className={`btn btn-sm ${autoRefreshEnabled ? 'btn-outline-success' : 'btn-outline-secondary'} py-0 px-2`}
              onClick={() => setAutoRefreshEnabled(!autoRefreshEnabled)}
              title={autoRefreshEnabled ? `Auto-refresh enabled (every ${autoRefreshInterval / 1000}s)` : "Enable auto-refresh"}
            >
              <i className={`fas fa-${autoRefreshEnabled ? 'pause' : 'play'} me-1`}></i>
              {autoRefreshEnabled ? 'Auto-refresh On' : 'Auto-refresh Off'}
            </button>
          )}
          {onRefresh && (
            <button
              className="btn btn-sm btn-outline-primary py-0 px-2"
              onClick={onRefresh}
              disabled={loading}
              title="Refresh activities"
            >
              {loading ? (
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
              ) : (
                <i className="fas fa-sync-alt"></i>
              )}
            </button>
          )}
        </div>
      </div>
      <div style={{ maxHeight: '400px', overflow: 'auto' }}>
        {loading ? (
          <div className="text-center py-3" style={{ height: '400px' }}>
            <div className="spinner-border spinner-border-sm me-2" role="status"></div>
            Loading activities...
          </div>
        ) : (!activities || activities.length === 0) ? (
          <div className="text-muted text-center py-2">
            <i className="fas fa-history me-2"></i>{emptyMessage}
          </div>
        ) : (
          activities
            .map((activity, index) => {
            const activityType = activity.activity_type?.slug || activity.activity_type || 'Unknown';
            const activityDate = activity.created_at || activity.activity_date;
            const metadata = activity.activity_metadata || {};
            const retellData = activity.retell_data || {};
            const callId = metadata.call_id || retellData.call_id;
            // Map call_status to event_type: 'analyzed' -> 'call_analyzed', 'ongoing' -> 'call_started', 'ended' -> 'call_ended'
            const callStatus = metadata.call_status || retellData.call_status;
            const eventType = metadata.event_type || (callStatus === 'analyzed' ? 'call_analyzed' : callStatus === 'ongoing' ? 'call_started' : callStatus === 'ended' ? 'call_ended' : null);
            const isLast = index === activities.length - 1;

            // Check if this is a feedback activity - show minimal details
            const isFeedbackActivity = activityType === 'FEEDBACK_SUBMITTED' || activityType === 'QUICK_FEEDBACK_SUBMITTED';

            // Only show status badge if it's different from the event type
            const showStatusBadge = callStatus && callStatus.toLowerCase() !== eventType?.toLowerCase();

            return (
              <div
                key={activity.id || index}
                id={`activity-${activity.id}`}
                className={`pb-1 mb-1 ${!isLast ? 'border-bottom' : ''}`}
                style={{ transition: 'background-color 0.3s ease' }}
              >
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <span className={`badge badge-sm ${isFeedbackActivity ? 'bg-success border' : getActivityTypeBadge(activityType)} mb-1`}>
                      {isFeedbackActivity ? (
                        <>
                          <i className="fas fa-comment-dots me-2"></i>
                          {activityType === 'FEEDBACK_SUBMITTED' ? 'FEEDBACK' : 'QUICK FEEDBACK SUBMITTED'}
                        </>
                      ) : (
                        activityType.toUpperCase().replace(/_/g, ' ')
                      )}
                    </span>
                    {showStatusBadge && (
                      <span className={`badge mx-2 badge-sm ${getStatusBadge(callStatus)}`}>
                        {callStatus}
                      </span>
                    )}
                    {getCommunicationChannels(activity).includes('sms') && (
                      <span className="badge badge-sm bg-success ms-1" title="SMS sent">
                        <i className="fas fa-sms"></i> SMS
                      </span>
                    )}
                    {getCommunicationChannels(activity).includes('email') && (
                      <span className="badge badge-sm bg-success ms-1" title="Email sent">
                        <i className="fas fa-envelope"></i> EMAIL
                      </span>
                    )}
                    {activity.is_automated && (
                      <span className="badge badge-sm bg-secondary ms-1" title="Automated activity">
                        <i className="fas fa-robot"></i>
                      </span>
                    )}
                    {activity.follow_up_required && (
                      <span className="badge badge-sm bg-warning ms-1" title="Follow-up required">
                        <i className="fas fa-clock"></i> Follow-up
                      </span>
                    )}
                    {activity.assigned_to_name && (
                      <span className="badge badge-sm bg-info ms-1" title="Assigned">
                        <i className="fas fa-user-tag"></i> {activity.assigned_to_name}
                      </span>
                    )}
                    {metadata.priority && (
                      <span className={`badge badge-sm ${metadata.priority === 'urgent' ? 'bg-danger' : 'bg-secondary'} ms-1`} title="Priority">
                        {metadata.priority}
                      </span>
                    )}
                  </div>
                  <p className="d-flex align-items-center text-muted mb-0">
                    {isAnkrAdmin && !isFeedbackActivity && (
                    <span className="btn btn-outline-secondary border-0 me-2"
                        onClick={() => togglePayload(activity.id)}
                        title="View webhook payload for debugging"
                        disabled={loadingPayloads[activity.id]}
                      >
                        {loadingPayloads[activity.id] ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                            Loading...
                          </>
                        ) : (
                          <>
                            <i className={`fas fa-${expandedPayloads[activity.id] ? 'clipboard' : 'clipboard-list'}`}></i>
                          </>
                        )}
                      </span>
                    )}
                    {new Date(activityDate).toLocaleDateString()} {new Date(activityDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
                {/* Show performed_by for feedback activities */}
                {isFeedbackActivity && activity.performed_by_name && (
                  <div className="text-muted small mb-2">
                    <i className="fas fa-user me-1"></i>By: {activity.performed_by_name}
                  </div>
                )}
                {/* Skip detailed content for feedback activities - only show badge + timestamp */}
                {!isFeedbackActivity && (
                <div>
                  {/* Only show action for manual (non-automated) activities to avoid redundancy */}
                  {activity.action && !activity.is_automated && (
                    <div className="text-dark small mt-1">
                      <span className="badge bg-light text-dark border me-1">{activity.action}</span>
                    </div>
                  )}
                  {activity.notes && (
                    <div className="text-muted small mt-1">
                      <div>
                        {activity.notes.split('\n').map((line, lineIdx) => (
                          <div key={lineIdx}>
                            {line}
                            {hasCommunicationData(activity) && (line.includes('SMS sent') || line.includes('EMAIL sent')) && (
                              <button
                                className="btn btn-link btn-sm p-0 ms-2"
                                onClick={() => toggleCommunication(activity.id)}
                                title="View communication content"
                                style={{ verticalAlign: 'baseline' }}
                              >
                                <i className={`fas fa-eye ${expandedCommunications[activity.id] ? 'text-primary' : 'text-muted'}`}></i>
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                      {hasCommunicationData(activity) && expandedCommunications[activity.id] && (
                        <div className="mt-2 p-2 bg-light border rounded">
                          <strong className="text-primary">Communication Details</strong>
                          {activity.novu_data.communications.map((comm, idx) => (
                            <div key={idx} className="mt-2 pt-2 border-top">
                              <div className="mb-1">
                                <span className={`badge ${comm.channel === 'sms' ? 'bg-success' : 'bg-info'}`}>
                                  {comm.channel.toUpperCase()}
                                </span>
                                <span className="text-muted ms-2 small">
                                  {new Date(comm.sent_at).toLocaleDateString()} {new Date(comm.sent_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </span>
                              </div>
                              {comm.channel === 'sms' && (
                                <>
                                  <div className="small">
                                    <strong>Phone:</strong> {comm.phone}
                                  </div>
                                  <div className="small">
                                    <strong>Message:</strong>
                                    <div className="bg-white p-2 border rounded mt-1" style={{ whiteSpace: 'pre-wrap' }}>
                                      {comm.message}
                                    </div>
                                  </div>
                                </>
                              )}
                              {comm.channel === 'email' && (
                                <>
                                  <div className="small">
                                    <strong>Email:</strong> {comm.email}
                                  </div>
                                  <div className="small">
                                    <strong>Subject:</strong> {comm.subject}
                                  </div>

                                  {/* Tracking Info */}
                                  {comm.tracking_id && (
                                    <div className="mt-2 p-2 bg-white border rounded">
                                      <div className="d-flex justify-content-between align-items-center">
                                        <div>
                                          <small className="text-muted">
                                            <i className="fas fa-satellite-dish me-1"></i>
                                            Tracking {comm.email_opened ? 'Active' : 'Enabled'}
                                          </small>
                                          {comm.email_opened && comm.opened_at && (
                                            <small className="text-success ms-2">
                                              <i className="fas fa-check-circle me-1"></i>
                                              Opened {new Date(comm.opened_at).toLocaleString()}
                                            </small>
                                          )}
                                        </div>
                                        <button
                                          className="btn btn-sm btn-outline-secondary border-0 py-0 px-1"
                                          onClick={() => {
                                            const trackingUrl = `${window.location.origin}/api/admin/sales/track/email/${comm.tracking_id}`;
                                            navigator.clipboard.writeText(trackingUrl);
                                          }}
                                          title="Copy tracking pixel URL"
                                        >
                                          <i className="fas fa-link"></i>
                                        </button>
                                      </div>
                                    </div>
                                  )}

                                  <div className="small mt-2">
                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                      <strong>Body:</strong>
                                      <button
                                        className="btn btn-sm btn-outline-primary border-0 py-0 px-1"
                                        onClick={() => {
                                          const bodyText = comm.html_body || comm.body || '';
                                          navigator.clipboard.writeText(bodyText);
                                        }}
                                        title="Copy to clipboard"
                                      >
                                        <i className="fas fa-copy"></i>
                                      </button>
                                    </div>
                                    <div
                                      className="bg-white p-2 border rounded mt-1"
                                      style={{ maxHeight: '300px', overflowY: 'auto', fontSize: '0.85rem' }}
                                      dangerouslySetInnerHTML={{
                                        __html: comm.html_body || `<pre style="white-space: pre-wrap; margin: 0;">${comm.body || 'No body available'}</pre>`
                                      }}
                                    />
                                  </div>
                                </>
                              )}
                              {isAnkrAdmin && (<div className="small text-muted mt-1">
                                <strong>Workflow:</strong> {comm.workflow_id}
                              </div>)}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  {activity.follow_up_required && activity.follow_up_date && (
                    <div className="text-warning small">
                      <i className="fas fa-calendar-alt me-1"></i>
                      Follow-up: {new Date(activity.follow_up_date).toLocaleDateString()} {new Date(activity.follow_up_date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  )}
                  {activity.follow_up_notes && (
                    <div className="text-muted small fst-italic">
                      <i className="fas fa-sticky-note me-1"></i>{activity.follow_up_notes}
                    </div>
                  )}
                  {activity.performed_by_name && (
                    <div className="text-muted small">
                      <i className="fas fa-user me-1"></i>By: {activity.performed_by_name}
                    </div>
                  )}
                  {metadata.to_number && (
                    <div className="text-muted small">
                      <i className="fas fa-phone me-1"></i>{metadata.to_number}
                    </div>
                  )}
                  {metadata.agent_id && (
                    <div className="text-muted small">
                      <i className="fas fa-robot me-1"></i>Agent: {metadata.agent_id}
                    </div>
                  )}
                  {callId && (
                    <div className="d-flex justify-content-between align-items-center mt-1">
                      <code className="small" title="Call ID">
                        {callId}
                      </code>
                      {onViewCall && eventType === 'call_analyzed' && (
                        <button
                          className="btn btn-outline-primary py-0 px-1"
                          onClick={() => onViewCall(callId, 'transcript')}
                          title="View call transcript"
                        >
                          <i className="fas fa-eye me-1"></i>View
                        </button>
                      )}
                    </div>
                  )}

                  {/* Webhook Payload Viewer - Ankr Admin Only */}
                  {isAnkrAdmin && (metadata._webhook_preview?.has_full_payload || metadata.webhook_payload || retellData.call_id) && (
                    <div className="mt-2" style={{fontSize: '0.9em'}}>
                      {expandedPayloads[activity.id] && !loadingPayloads[activity.id] && (
                        <div className="mt-2 p-2 bg-light border rounded">
                          {payloadErrors[activity.id] ? (
                            <div className="alert alert-danger py-1 px-2 mb-0 small">
                              <i className="fas fa-exclamation-circle me-1"></i>
                              {payloadErrors[activity.id]}
                            </div>
                          ) : (() => {
                            // Use fetched payload data if available, otherwise use what's already in metadata
                            const displayMetadata = payloadData[activity.id]?.activity_metadata || payloadData[activity.id]?.retell_data || metadata;
                            const displayRetellData = payloadData[activity.id]?.retell_data || retellData;
                            const webhookPayload = displayMetadata.webhook_payload || displayRetellData;

                            if (!webhookPayload) {
                              return <div className="text-muted">No webhook payload available</div>;
                            }

                            return (
                              <>
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                  <strong className="text-primary">WEBHOOK PAYLOAD</strong>
                                  <div>
                                    <button className="btn btn-outline-primary border-0"
                                    onClick={() => copyToClipboard(JSON.stringify(webhookPayload, null, 2), setCopyIconStates, activity.id)}
                                    title="Copy full payload to clipboard"
                                  >
                                    <i className={`fas ${getCopyIcon(copyIconStates[activity.id])}`}></i>
                                  </button>
                                  <button className="btn btn-outline-secondary border-0 "
                                    onClick={() => togglePayload(activity.id)}
                                    disabled={loadingPayloads[activity.id]}>
                                    <i className='fa fa-times'></i>
                                  </button>
                                  </div>
                                  </div>
                                {/* Collected Dynamic Variables */}
                                {webhookPayload.collected_dynamic_variables && (
                                  <div className="mb-2">
                                    <strong className="text-success">collected_dynamic_variables:</strong>
                                    <div className="ps-2">
                                      {webhookPayload.collected_dynamic_variables.vcx_event_status && (
                                        <div><strong className="text-danger">vcx_event_status:</strong> {webhookPayload.collected_dynamic_variables.vcx_event_status}</div>
                                      )}
                                      {webhookPayload.collected_dynamic_variables.vcx_event_priority && (
                                        <div><strong className="text-warning">vcx_event_priority:</strong> {webhookPayload.collected_dynamic_variables.vcx_event_priority}</div>
                                      )}
                                      {webhookPayload.collected_dynamic_variables.caller_intent && (
                                        <div><strong>caller_intent:</strong> {webhookPayload.collected_dynamic_variables.caller_intent}</div>
                                      )}
                                      {webhookPayload.collected_dynamic_variables.patient_name && (
                                        <div><strong>patient_name:</strong> {webhookPayload.collected_dynamic_variables.patient_name}</div>
                                      )}
                                      {webhookPayload.collected_dynamic_variables.patient_dob && (
                                        <div><strong>patient_dob:</strong> {webhookPayload.collected_dynamic_variables.patient_dob}</div>
                                      )}
                                    </div>
                                  </div>
                                )}

                                {/* Custom Analysis Data */}
                                {webhookPayload.call_analysis?.custom_analysis_data && (
                                  <div className="mb-2">
                                    <strong className="text-info">call_analysis.custom_analysis_data:</strong>
                                    <div className="ps-2">
                                      {webhookPayload.call_analysis.custom_analysis_data.debug_event_status && (
                                        <div><strong className="text-danger">debug_event_status:</strong> {webhookPayload.call_analysis.custom_analysis_data.debug_event_status}</div>
                                      )}
                                      {webhookPayload.call_analysis.custom_analysis_data.task_status && (
                                        <div><strong className="text-primary">task_status:</strong> {webhookPayload.call_analysis.custom_analysis_data.task_status}</div>
                                      )}
                                      {webhookPayload.call_analysis.custom_analysis_data.task_completed !== undefined && (
                                        <div><strong className="text-success">task_completed:</strong> {String(webhookPayload.call_analysis.custom_analysis_data.task_completed)}</div>
                                      )}
                                      {webhookPayload.call_analysis.custom_analysis_data.vcx_referral_task_status && (
                                        <div><strong className="text-warning">vcx_referral_task_status:</strong> {webhookPayload.call_analysis.custom_analysis_data.vcx_referral_task_status}</div>
                                      )}
                                      {webhookPayload.call_analysis.custom_analysis_data.vcx_referral_status && (
                                        <div><strong className="text-info">vcx_referral_status:</strong> {webhookPayload.call_analysis.custom_analysis_data.vcx_referral_status}</div>
                                      )}
                                    </div>
                                  </div>
                                )}

                                {/* Metadata - Referral/Task Context */}
                                {webhookPayload.metadata && (
                                  <div className="mb-2">
                                    <strong className="text-primary">metadata:</strong>
                                    <div className="ps-2">
                                      {webhookPayload.metadata.contact_name && (
                                        <div><strong>contact_name:</strong> {webhookPayload.metadata.contact_name}</div>
                                      )}
                                      {webhookPayload.metadata.patient_name && (
                                        <div><strong>patient_name:</strong> {webhookPayload.metadata.patient_name}</div>
                                      )}
                                      {webhookPayload.metadata.facility && (
                                        <div><strong>facility:</strong> {webhookPayload.metadata.facility}</div>
                                      )}
                                      {webhookPayload.metadata.referral_id && (
                                        <div><strong>referral_id:</strong> {webhookPayload.metadata.referral_id}</div>
                                      )}
                                      {webhookPayload.metadata.task_id && (
                                        <div><strong>task_id:</strong> {webhookPayload.metadata.task_id}</div>
                                      )}
                                      {webhookPayload.metadata.entity_type && (
                                        <div><strong>entity_type:</strong> {webhookPayload.metadata.entity_type}</div>
                                      )}
                                    </div>
                                  </div>
                                )}

                                {/* Retell LLM Dynamic Variables */}
                                {webhookPayload.retell_llm_dynamic_variables && Object.keys(webhookPayload.retell_llm_dynamic_variables).length > 0 && (
                                  <div className="mb-2">
                                    <strong className="text-secondary">retell_llm_dynamic_variables:</strong>
                                    <pre className="small mb-0 ps-2" style={{ maxHeight: '100px', overflow: 'auto' }}>
                                      {JSON.stringify(webhookPayload.retell_llm_dynamic_variables, null, 2)}
                                    </pre>
                                  </div>
                                )}

                                {/* Full Payload (Collapsible) */}
                                <details className="mt-2">
                                  <summary className="text-muted" style={{ cursor: 'pointer' }}>
                                    <strong>Full webhook_payload JSON</strong>
                                  </summary>
                                  <pre className="small bg-white p-2 border rounded mt-1" style={{ maxHeight: '300px', overflow: 'auto' }}>
                                    {JSON.stringify(webhookPayload, null, 2)}
                                  </pre>
                                </details>
                              </>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default ActivityTimeline;
